import React, { useEffect, useMemo, useState } from "react";
import { toApiAbsoluteUrl } from "../api/client";
import { Navigation } from "lucide-react";
import { Link } from "react-router-dom";
import { FiMapPin } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

export default function ListingCard({ listing }) {
  const [currentImage, setCurrentImage] = useState(0);

  const images = useMemo(() => {
    const raw = Array.isArray(listing?.images) ? listing.images : [];
    return raw
      .map((img) => (typeof img === "string" ? img : img?.url))
      .filter(Boolean)
      .map((u) => toApiAbsoluteUrl(u));
  }, [listing]);

  const PLACEHOLDER_IMAGE = `https://placehold.co/800x450/png?text=${listing.name}`;
  const displayImages = images.length > 0 ? images : [PLACEHOLDER_IMAGE];

  const handlePrev = () => {
    setCurrentImage((prev) =>
      prev === 0 ? displayImages.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentImage((prev) => (prev + 1) % displayImages.length);
  };

  useEffect(() => {
    if (displayImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentImage((prev) => (prev + 1) % displayImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [displayImages.length]);

  const priceDisplay = useMemo(() => {
    if (typeof listing?.price === "number") {
      try {
        return new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(listing.price);
      } catch {
        return `₹${listing.price}`;
      }
    }
    return null;
  }, [listing]);

  const providerName = useMemo(() => {
    return (
      listing?.provider?.providerProfile?.companyName ||
      listing?.provider?.email ||
      ""
    );
  }, [listing]);

  const providerId = useMemo(() => {
    const profile = listing?.provider?.providerProfile;
    return profile?.id || profile?._id || "";
  }, [listing]);

  const providerSlug = useMemo(() => {
    const name = providerName || "";
    const s = String(name).trim();
    if (!s) return "";
    return s
      .toLowerCase()
      .replace(/[^a-z0-9\s-_]/g, "")
      .replace(/\s+/g, "-");
  }, [providerName]);

  const companyType = useMemo(() => {
    return (
      listing?.provider?.providerProfile?.companyType ||
      listing?.companyType ||
      ""
    );
  }, [listing]);

  const callNumber = useMemo(() => {
    return (
      listing?.phoneNumber ||
      listing?.provider?.providerProfile?.phoneNumber ||
      ""
    );
  }, [listing]);

  const categoryName = useMemo(() => listing?.category?.name || "", [listing]);
  const subcategoryName = useMemo(
    () => listing?.subcategory?.name || "",
    [listing]
  );

  const distanceText =
    typeof listing?.distanceKm === "number" ? `${listing.distanceKm} km` : null;

  // Prepare Google Maps URL from coordinates if available
  const coordinates = Array.isArray(listing?.location?.coordinates)
    ? listing.location.coordinates
    : null;
  let mapsUrl = null;
  let lat = null;
  let lng = null;
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    lng = coordinates[0];
    lat = coordinates[1];
    if (typeof lat === "number" && typeof lng === "number") {
      mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    }
  }

  return (
    <div
      className="
  w-full max-w-screen-3xl mx-auto p-2 rounded-xl overflow-hidden shadow-xl shadow-blue-950/20 
  flex flex-row sm:flex-col bg-white sm:mb-6
"
    >
      {/* IMAGE SECTION */}
      <div
        className="
    relative 
    w-32 h-full         /* mobile size */
    sm:w-full sm:h-60   /* web size */
    overflow-hidden
  "
      >
        <img
          src={displayImages[currentImage]}
          alt={listing?.name}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = PLACEHOLDER_IMAGE;
          }}
          className="
        w-full h-full object-cover 
        rounded-lg sm:rounded-lg 
      "
        />

        {/* Arrows - Only on Web */}
        {displayImages.length > 1 && (
          <div className="hidden sm:block">
            <button
              onClick={handlePrev}
              className="absolute top-1/2 left-2 -translate-y-1/2 text-white bg-black/40 rounded-full p-1.5 hover:bg-black/70 z-10"
            >
              ←
            </button>
            <button
              onClick={handleNext}
              className="absolute top-1/2 right-2 -translate-y-1/2 text-white bg-black/40 rounded-full p-1.5 hover:bg-black/70 z-10"
            >
              →
            </button>
          </div>
        )}
      </div>

      {/* CONTENT SECTION */}
      <div
        className="
    flex-1 flex flex-col justify-between 
     sm:p-6 
    bg-white rounded-r-xl 
    space-y-3 sm:space-y-4
    min-w-0
    p-2
  "
      >
        <div className="flex-1 min-w-0">
          <h2
            className="
  text-sm sm:text-xl text-gray-800 poppins-bold mb-1 sm:mb-2
  whitespace-nowrap overflow-hidden text-ellipsis capitalize 
"
          >
            {listing?.name}
          </h2>

          {priceDisplay && (
            <p className="text-xs sm:text-md md:text-lg text-green-600 mb-1 sm:mb-2 poppins-semibold tabular-nums font-mono">
              {priceDisplay}
            </p>
          )}

          <div className="text-gray-700 text-xs sm:text-base space-y-1 sm:space-y-2 poppins-regular">
            <p className="font-bold uppercase text-xs sm:text-base  whitespace-nowrap overflow-hidden">
              {providerId || providerSlug ? (
                <Link
                  to={`/${encodeURIComponent(providerId || providerSlug)}`}
                  className="hover:underline"
                >
                  {providerName}
                </Link>
              ) : (
                <span>{providerName}</span>
              )}
            </p>

            {listing?.address && (
              <p className="flex items-center max-w-full">
                <span className="truncate md:whitespace-normal md:truncate-none">
                  📍 {listing.address}
                </span>

                {mapsUrl && (
                  <button
                    onClick={() => window.open(mapsUrl, "_blank", "noopener")}
                    className="ml-2 text-blue-600 hover:text-blue-800 flex-shrink-0"
                    title={`Open in Maps (${lat}, ${lng})`}
                    aria-label="Open in Google Maps"
                  >
                    <Navigation size={16} />
                  </button>
                )}
              </p>
            )}

            {distanceText && (
              <p className="text-xs sm:text-base">🧭 {distanceText}</p>
            )}

            {listing?.description && (
              <>
                {/* MOBILE: Sliced text */}
                <p className="text-xs sm:hidden">
                  {listing.description.length > 60
                    ? listing.description.slice(0, 25) + "..."
                    : listing.description}
                </p>

                {/* WEB: Full text */}
                <p className="hidden sm:block text-sm">
                  {listing.description.slice(0, 100)}...
                </p>
              </>
            )}
          </div>

          {/* Directions */}
          {/* <p className="pt-1 sm:pt-3 poppins-semibold text-xs sm:text-base">
            {mapsUrl && (
              <button
                onClick={() => window.open(mapsUrl, "_blank", "noopener")}
                className="inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                Get the directions.
                <span className="text-green-600 ml-1 hover:scale-110 transition-all duration-300 ease-in-out">
                  <FiMapPin size={16} className="sm:size-18" />
                </span>
              </button>
            )}
          </p> */}
        </div>
        <div
          className="flex items-center gap-1 sm:gap-2 pt-1 
                whitespace-nowrap overflow-x-auto scrollbar-hide"
        >
          {categoryName && (
            <span
              className="px-2 py-0.5 text-[10px] sm:text-xs rounded-full bg-gray-100 
                     border border-gray-200 text-gray-700"
            >
              {categoryName}
            </span>
          )}

          {subcategoryName && (
            <span
              className="px-2 py-0.5 text-[10px] sm:text-xs rounded-full bg-gray-100 
                     border border-gray-200 text-gray-700"
            >
              {subcategoryName}
            </span>
          )}
        </div>

        {/* CALL & WHATSAPP */}
        <div className="mt-3 sm:mt-4 flex md:justify-between gap-2">
          <a href={callNumber ? `tel:${callNumber}` : undefined}>
            <button
              className="px-6 py-2 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-base font-semibold shadow-md 
          bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:shadow-lg hover:scale-[1.02] transition"
            >
              {/* MOBILE ONLY: icon */}
              <span className="sm:hidden">📞</span>

              {/* WEB ONLY: icon + text */}
              <span className="hidden sm:flex items-center gap-1">
                📞 Call Now
              </span>
            </button>
          </a>
          <a
            href={callNumber ? `https://wa.me/${callNumber}` : undefined}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button
              className="px-6 py-2 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-base font-semibold shadow-md 
          bg-gradient-to-r from-green-500 to-green-700 text-white hover:shadow-lg hover:scale-[1.02] transition"
            >
              {/* MOBILE ONLY: icon */}
              <span className="sm:hidden">
                <FaWhatsapp size={16} />
              </span>

              {/* WEB ONLY: icon + text */}
              <span className="hidden sm:flex items-center gap-1">
                <FaWhatsapp size={16} /> WhatsApp
              </span>
            </button>
          </a>
        </div>

        {/* <div className="flex justify-between items-center pt-2 sm:pt-3 border-t border-gray-300 text-[10px] sm:text-sm poppins-semibold uppercase">
          <span>🌐 {categoryName || ""}</span>
          <span>{companyType}</span>
        </div> */}
      </div>
    </div>
  );
}
